import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  GET_BROADCASTER_CONFIG,
  UPDATE_BROADCASTER_CONFIG,
} from "../../../../queries/broadcasterConfigQueries";
import LoadingPage from "../../../LoadingPage";
import DiscordServerSetting from "./DiscordServerSetting";
import VipRoleSetting from "./VipRoleSetting";

const BroadcasterSettings = () => {
  const { data, loading, error } = useQuery(GET_BROADCASTER_CONFIG);
  const [updateConfig, { loading: updating }] = useMutation(
    UPDATE_BROADCASTER_CONFIG,
    {
      refetchQueries: [{ query: GET_BROADCASTER_CONFIG }],
    },
  );

  const [isStreamStatsEnabled, setIsStreamStatsEnabled] = useState(false);
  const [isSoundboardEnabled, setIsSoundboardEnabled] = useState(true);
  const [isDenyWarEnabled, setIsDenyWarEnabled] = useState(true);
  const [notificationDiscordId, setNotificationDiscordId] = useState("");

  // Update local state when data loads
  useEffect(() => {
    if (data?.getBroadcasterConfig) {
      setIsStreamStatsEnabled(data.getBroadcasterConfig.isStreamStatsEnabled);
      setIsSoundboardEnabled(
        data.getBroadcasterConfig.isSoundboardEnabled ?? true,
      );
      setIsDenyWarEnabled(data.getBroadcasterConfig.isDenyWarEnabled ?? true);
      setNotificationDiscordId(
        data.getBroadcasterConfig.notificationDiscordId ?? "",
      );
    }
  }, [data]);

  const handleToggleStreamStats = async (enabled: boolean) => {
    try {
      setIsStreamStatsEnabled(enabled);
      await updateConfig({
        variables: {
          input: {
            isStreamStatsEnabled: enabled,
          },
        },
      });
      toast.success(
        `Stream stats ${enabled ? "enabled" : "disabled"} successfully`,
      );
    } catch (err) {
      console.error("Error updating config:", err);
      toast.error("Failed to update settings");
      // Revert on error
      setIsStreamStatsEnabled(!enabled);
    }
  };

  const handleToggleSoundboard = async (enabled: boolean) => {
    try {
      setIsSoundboardEnabled(enabled);
      await updateConfig({
        variables: {
          input: {
            isSoundboardEnabled: enabled,
          },
        },
      });
      toast.success(
        `Soundboard ${enabled ? "enabled" : "disabled"} successfully`,
      );
    } catch (err) {
      console.error("Error updating config:", err);
      toast.error("Failed to update settings");
      // Revert on error
      setIsSoundboardEnabled(!enabled);
    }
  };

  const handleToggleDenyWar = async (enabled: boolean) => {
    try {
      setIsDenyWarEnabled(enabled);
      await updateConfig({
        variables: {
          input: {
            isDenyWarEnabled: enabled,
          },
        },
      });
      toast.success(
        `Deny wars ${enabled ? "enabled" : "disabled"} successfully`,
      );
    } catch (err) {
      console.error("Error updating config:", err);
      toast.error("Failed to update settings");
      // Revert on error
      setIsDenyWarEnabled(!enabled);
    }
  };

  const savedDiscordId =
    data?.getBroadcasterConfig?.notificationDiscordId ?? "";

  const handleSaveDiscordId = async () => {
    const trimmed = notificationDiscordId.trim();
    if (trimmed && !/^\d{17,20}$/.test(trimmed)) {
      toast.error(
        "That doesn't look like a Discord user ID - it should be 17-20 digits",
      );
      return;
    }
    try {
      await updateConfig({
        variables: {
          input: {
            notificationDiscordId: trimmed,
          },
        },
      });
      toast.success(
        trimmed ? "Discord ID saved" : "Discord ID cleared",
      );
    } catch (err) {
      console.error("Error updating config:", err);
      toast.error("Failed to save Discord ID");
      setNotificationDiscordId(savedDiscordId);
    }
  };

  if (loading) return <LoadingPage />;
  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading settings: {error.message}</span>
      </div>
    );
  }

  const config = data?.getBroadcasterConfig;

  return (
    <div className="min-h-screen bg-base-200 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-base-content mb-2">
            ⚙️ Broadcaster Settings
          </h1>
          <p className="text-base-content/70">
            Manage your broadcaster configuration and preferences
          </p>
        </div>

        {/* Settings Cards */}
        <div className="space-y-4">
          {/* Discord Integration Settings */}
          <div className="collapse collapse-arrow bg-base-100 shadow-lg">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-xl font-medium">
              🔔 Discord Integration
            </div>
            <div className="collapse-content">
              <div className="space-y-4">
                {/* Stream Stats Toggle */}
                <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base-content">
                      Stream Statistics Messages
                    </h3>
                    <p className="text-sm text-base-content/60 mt-1">
                      Automatically post stream statistics to Discord when you
                      go offline
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={isStreamStatsEnabled}
                    onChange={(e) => handleToggleStreamStats(e.target.checked)}
                    disabled={updating}
                  />
                </div>

                {/* Notification Discord ID */}
                <div className="p-4 bg-base-200 rounded-lg">
                  <h3 className="font-semibold text-base-content">
                    Your Discord User ID
                  </h3>
                  <p className="text-sm text-base-content/60 mt-1">
                    Where private redeems get sent. Without it, you only get a
                    heads up in the offline tips channel that something private
                    was redeemed — not what it was.
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">
                    In Discord: Settings → Advanced → Developer Mode, then right
                    click your name and pick Copy User ID.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={notificationDiscordId}
                      onChange={(e) =>
                        setNotificationDiscordId(e.target.value)
                      }
                      placeholder="e.g. 123456789012345678"
                      className="input input-bordered flex-1"
                      disabled={updating}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveDiscordId}
                      disabled={
                        updating || notificationDiscordId === savedDiscordId
                      }
                    >
                      Save
                    </button>
                  </div>
                  {savedDiscordId ? (
                    <p className="text-xs text-success mt-2">
                      ✓ Private redeems will be sent to your DMs
                    </p>
                  ) : (
                    <p className="text-xs text-warning mt-2">
                      Not set — private redeem details can't be DM'd to you
                    </p>
                  )}
                </div>

                <DiscordServerSetting />

                <VipRoleSetting />
              </div>
            </div>
          </div>

          {/* Stream Features Section */}
          <div className="collapse collapse-arrow bg-base-100 shadow-lg">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title text-xl font-medium">
              🎮 Stream Features
            </div>
            <div className="collapse-content">
              <div className="space-y-4">
                {/* Soundboard Toggle */}
                <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base-content">
                      Soundboard
                    </h3>
                    <p className="text-sm text-base-content/60 mt-1">
                      Allow viewers to play sounds during your stream
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={isSoundboardEnabled}
                    onChange={(e) => handleToggleSoundboard(e.target.checked)}
                    disabled={updating}
                  />
                </div>

                {/* Deny War Toggle */}
                <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base-content">
                      Deny Wars
                    </h3>
                    <p className="text-sm text-base-content/60 mt-1">
                      Run deny wars. While off, denies and undenies are still
                      tracked but the war stops counting and viewers can't see
                      it
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={isDenyWarEnabled}
                    onChange={(e) => handleToggleDenyWar(e.target.checked)}
                    disabled={updating}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Future Settings Sections */}
          <div className="collapse collapse-arrow bg-base-100 shadow-lg">
            <input type="checkbox" />
            <div className="collapse-title text-xl font-medium">
              🎨 Appearance & Branding
            </div>
            <div className="collapse-content">
              <div className="text-base-content/60 text-sm">Coming soon...</div>
            </div>
          </div>

          <div className="collapse collapse-arrow bg-base-100 shadow-lg">
            <input type="checkbox" />
            <div className="collapse-title text-xl font-medium">
              🎮 Stream Features
            </div>
            <div className="collapse-content">
              <div className="text-base-content/60 text-sm">Coming soon...</div>
            </div>
          </div>
        </div>

        {/* Info Footer */}
        <div className="mt-8 p-4 bg-info/10 border border-info/20 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-info text-xl">💡</span>
            <div className="flex-1">
              <p className="text-sm text-info">
                Changes are saved automatically. More settings will be added in
                future updates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcasterSettings;
